import { date as dateUtils } from '@ipfabric/utils';
import { isValid } from 'date-fns';
import humanizeDuration from 'humanize-duration';
import get from 'lodash/get';

const XML_CHAR_MAP = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  '"': '&quot;',
  "'": '&apos;',
};

const ucfirst = (s) => s.substr(0, 1).toUpperCase() + s.substring(1);

const getValue = (data) => {
  const textValue = get(data, 'data', data);
  if (textValue === '' || textValue === null || textValue === undefined) {
    return 'N/A';
  }
  return textValue;
};

const formatTimestamp = (data, multiple = 1) => {
  try {
    return humanizeDuration(getValue(data) * multiple);
  } catch {
    return 'N/A';
  }
};

const formatDate = (data, timezone, format) => {
  try {
    const date = dateUtils.utcToUserTimeZone(getValue(data), timezone);
    if (!isValid(date)) {
      return 'N/A';
    }
    return dateUtils.formatUtcTimestampInTimeZone(
      getValue(data),
      timezone,
      format,
    );
  } catch {
    return 'N/A';
  }
};

function escapeXml(s) {
  if (typeof s !== 'string') {
    return s;
  }
  return (
    s
      .replace(/[<>&"']/g, (ch) => XML_CHAR_MAP[ch] || '')
      // NIM-4224 Remove non-printable characters
      .replace(/[^\x20-\x7E]+/g, '')
  );
}

export const mapSeverityColumn = (col, originalCol) => {
  const text = escapeXml(getValue(col));
  const severity = get(originalCol, 'severity', null);
  let color;
  switch (severity) {
    case 0:
      color = '00a65a';
      break;
    case 10:
      color = '00c0ef';
      break;
    case 20:
      color = 'f39c12';
      break;
    case 30:
      color = 'f56954';
      break;
    default:
      return `<w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:sz w:val="16"/></w:rPr><w:t>${text}</w:t></w:r></w:p>`;
  }
  return `<w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:color w:val="${color}"/><w:sz w:val="16"/></w:rPr><w:t>${text}</w:t></w:r></w:p>`;
};

const mapTableRow = (tableKey, row, timezone, dateFormat) => {
  return Object.keys(row).reduce((acc, colKey) => {
    const value = row[colKey];
    // Special rules for mapping special tables & cols
    if (
      ['recentReloads', 'unexpectedReloads'].includes(tableKey) &&
      colKey === 'uptime'
    ) {
      acc[colKey] = mapSeverityColumn(formatTimestamp(value, 1000), value);
      return acc;
    }
    if (
      ['ospfStab', 'bgpStab', 'eigrpStab', 'isisStab'].includes(tableKey) &&
      colKey === 'currStateTime'
    ) {
      acc[colKey] = mapSeverityColumn(formatTimestamp(value, 1000), value);
      return acc;
    }
    if (
      ['endOfSupport'].includes(tableKey) &&
      ['endSale', 'endMaintenance', 'endSupport'].includes(colKey)
    ) {
      acc[colKey] = mapSeverityColumn(
        formatDate(value, timezone, dateFormat),
        value,
      );
      return acc;
    }

    acc[colKey] = mapSeverityColumn(value, value);
    return acc;
  }, {});
};

export const getCountPropertyName = (key) => `${key}Count`;

export const addTruncateMessage = (source, destination, tableKey, limit) => {
  const placeholderName = `${tableKey}TruncateMessage`;
  const count = source[getCountPropertyName(tableKey)];
  if (count > limit) {
    destination[placeholderName] = `${limit} out of ${count} `;
  }
  return '';
};

const mapTables = (json, timezone, dateFormat, limit) => {
  return Object.keys(json).reduce((acc, tableKey) => {
    if (Array.isArray(json[tableKey])) {
      acc[`has${ucfirst(tableKey)}`] = json[tableKey].length > 0;
      addTruncateMessage(json, acc, tableKey, limit);
      if (tableKey === 'dashboardTables') {
        acc[tableKey] = json[tableKey];
      } else {
        acc[tableKey] = json[tableKey].map((row) =>
          mapTableRow(tableKey, row, timezone, dateFormat),
        );
      }
      return acc;
    }
    acc[tableKey] = json[tableKey];
    return acc;
  }, {});
};

export { mapTables, getValue };
